/**
 * Komal OS - CRM: the card scan queue.
 *
 * Cards land here (status='queued') when Groq's shared rate limit is hit
 * mid-scan - see app/crm/card_scan_worker.py. Nothing is lost; the
 * background worker drains this in order. This page just shows where
 * everything stands, per the spec's explicit ask: "show the pending time".
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Clock, CheckCircle2, XCircle, Camera } from 'lucide-react';
import { listScanJobs, mediaUrl } from '../../services/crmService';

const STATUS_META = {
  pending: { label: 'Starting…', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  processing: { label: 'Scanning…', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  queued: { label: 'Queued', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  done: { label: 'Ready to review', cls: 'bg-green-50 text-green-700 border-green-200' },
  failed: { label: 'Failed', cls: 'bg-red-50 text-red-700 border-red-200' },
};

const formatEta = (seconds) => {
  if (!seconds) return null;
  if (seconds < 90) return `~${seconds}s`;
  return `~${Math.round(seconds / 60)}m`;
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  return `${Math.round(mins / 60)}h ago`;
};

export default function ScanQueuePage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const load = async () => {
    try {
      const res = await listScanJobs(!showAll);
      setJobs(res.data.jobs);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, 8000); // live-ish view without a manual refresh
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAll]);

  const queuedCount = jobs.filter((j) => j.status === 'queued').length;

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-white">Scan queue</h1>
            <p className="text-sm text-white/70 mt-0.5">
              Cards wait here when Groq's rate limit is hit — nothing is lost, just paced out.
            </p>
          </div>
          <Link
            to="/crm/scan"
            className="flex items-center gap-1.5 text-xs font-medium text-white/90 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3 py-2"
          >
            <Camera size={14} /> Scan a card
          </Link>
        </div>

        {queuedCount > 0 && (
          <div className="glass-card p-4 mb-4 flex items-center gap-3">
            <Loader2 size={16} className="animate-spin text-amber-600 shrink-0" />
            <div className="text-sm">
              <span className="font-semibold text-gray-800">{queuedCount} card{queuedCount > 1 ? 's' : ''} queued</span>
              <span className="text-gray-500"> — the background worker is draining these one at a time. Keep this page open or check back later.</span>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={() => setShowAll(false)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border ${!showAll ? 'bg-komal-blue text-white border-komal-blue' : 'bg-white/10 text-white/80 border-white/20'}`}
          >
            My scans
          </button>
          <button
            onClick={() => setShowAll(true)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border ${showAll ? 'bg-komal-blue text-white border-komal-blue' : 'bg-white/10 text-white/80 border-white/20'}`}
          >
            Whole team
          </button>
        </div>

        <div className="glass-card">
          {loading ? (
            <p className="text-gray-500 text-sm p-5">Loading…</p>
          ) : jobs.length === 0 ? (
            <p className="text-gray-400 text-sm p-5">No scans yet. <Link to="/crm/scan" className="text-komal-blue underline">Scan a card</Link> to get started.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {jobs.map((j) => {
                const meta = STATUS_META[j.status] || STATUS_META.pending;
                const isMulti = j.scan_mode === 'multi';
                const cardCount = isMulti ? (j.result?.cards?.length ?? null) : null;
                const name = isMulti
                  ? (cardCount == null ? 'Multiple cards' : `${cardCount} card${cardCount === 1 ? '' : 's'} found`)
                  : (j.result?.name || j.result?.company || 'Unnamed card');
                const eta = formatEta(j.eta_seconds);
                return (
                  <li key={j.id} className="px-4 py-3 flex items-center gap-3">
                    {j.image_url ? (
                      <img src={mediaUrl(j.image_url)} alt="" className="w-10 h-10 rounded-lg object-cover border border-gray-200 shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-800 truncate">{name}</div>
                      <div className="text-xs text-gray-500">
                        {timeAgo(j.created_at)}
                        {isMulti ? ' · multi-card' : ''}
                        {j.status === 'queued' && j.queue_position ? ` · position ${j.queue_position}` : ''}
                        {j.status === 'queued' && eta ? ` · next try in ${eta}` : ''}
                      </div>
                    </div>
                    <span className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border ${meta.cls}`}>
                      {['pending', 'processing'].includes(j.status) && <Loader2 size={11} className="animate-spin" />}
                      {j.status === 'queued' && <Clock size={11} />}
                      {j.status === 'done' && <CheckCircle2 size={11} />}
                      {j.status === 'failed' && <XCircle size={11} />}
                      {meta.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
